/**
 * The browser's memory of how this visitor first arrived.
 *
 * A visitor who clicks a Meta or Google ad carries a click id in the URL, but
 * only on that one page load. Plenty of people click an ad, look around, leave,
 * and come back a day later by bookmark or by typing the address — and that
 * later visit has no click id in its URL at all.
 *
 * So the fields are cached here, in localStorage, where they survive closing the
 * tab and signing out (`local-storage-hygiene.ts` lists the key as a survivor).
 *
 * This used to live privately inside `telemetry.service.ts` and was read only by
 * Mixpanel and Refiner. It moved here so the CHECKOUT can read it too: the blob
 * a checkout sends to the server was built from the URL alone, so a returning
 * buyer's purchase reached Meta with nothing to match it against the ad that
 * produced it. Measured on 2026-09-09, that was at least 43% of ad-driven web
 * subscribers.
 *
 * The checkout reads it in `getTrackingDataForCheckout`
 * (`client/src/services/url-query-service.ts`) and merges onto a COPY. That
 * placement is load-bearing rather than tidy — the bag `getTrackingData()`
 * returns is shared with `TelemetryService`, and putting a remembered click id
 * into it would make that service treat the visit as a fresh campaign arrival
 * and overwrite the remembered campaign fields with only the click ids.
 */

const kCachedUtmsKey = 'cachedUtms';

/**
 * The marketing fields worth remembering across visits.
 *
 * `fbclid_ts` is the moment the ad click happened. It is cached BESIDE the click
 * id it belongs to, and neither is any use to the checkout without the other:
 * Meta's `fbc` value embeds the click time, and a trial-to-paid conversion fires
 * about 14 days later, so a click id reported with the wrong time describes a
 * click that never happened (#4045). Add one of the pair here and you must add
 * the other.
 */
export const CACHED_TRACKING_KEYS = [
  'utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'utm_clid', 'gclid', 'fbclid', 'fbclid_ts',
] as const;

/**
 * The subset the CHECKOUT is allowed to restore from the cache — the ad click
 * ids and nothing else.
 *
 * The `utm_*` fields are deliberately NOT here, and this is a safety rule rather
 * than a tidiness one. The server picks the Paddle SANDBOX environment when a
 * checkout's `utm_source` is the `e2e-test` marker (see `isE2ETestUtmSource`),
 * so restoring a cached `utm_source` could send a real buyer's real purchase
 * down the sandbox rail because they once opened a test link. Which environment
 * a checkout uses stays decided by the URL alone, exactly as before.
 */
const CHECKOUT_RESTORABLE_KEYS = ['fbclid', 'fbclid_ts', 'gclid'] as const;

/** Does this tracking bag carry anything worth caching? */
export function hasCacheableTrackingFields(data: any): boolean {
  return !!data && CACHED_TRACKING_KEYS.some(f => !!data[f]);
}

/** What the browser remembers, or null when it remembers nothing. */
export function loadCachedTracking(): { [key: string]: string } | null {
  try {
    const raw = localStorage.getItem(kCachedUtmsKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // storage blocked, or a damaged value: the browser remembers nothing
    return null;
  }
}

/**
 * Remember the cacheable fields of this bag.
 *
 * The stored object is REPLACED rather than merged, which is deliberate and
 * predates this file: a later visit through a different campaign describes a
 * different arrival, and merging would blend two of them into one that never
 * happened.
 */
export function cacheTrackingFields(data: any): void {
  if (!data) return;
  const cached: { [key: string]: string } = {};
  for (const f of CACHED_TRACKING_KEYS) {
    if (data[f]) cached[f] = data[f];
  }
  if (Object.keys(cached).length > 0) {
    try {
      localStorage.setItem(kCachedUtmsKey, JSON.stringify(cached));
    } catch {
      // storage blocked or full: the browser will not remember this arrival
    }
  }
}

/**
 * The ad click ids to add to a tracking bag the URL did not supply any for.
 *
 * Returns an empty object whenever the URL already carried a click id — a fresh
 * click always wins, and mixing the two is the failure #4045 describes: a NEW
 * click id paired with an OLD timestamp reports a click that never happened.
 *
 * A cached click id with no timestamp beside it is still restored. That is not
 * the mixing case: the server treats a missing timestamp as "we did not record
 * it" and falls back to the report time, a documented fallback that exists for
 * exactly this shape — a click id captured before we started recording times.
 * Caches written before `fbclid_ts` was cached look like that until the visitor
 * next arrives through a tagged link.
 *
 * Pure, so every combination can be asserted without a browser.
 */
export function resolveClickIdsFromCache(
  urlData: { [key: string]: any },
  cached: { [key: string]: string } | null,
): { [key: string]: string } {
  // if the URL supplied either click id, the cache has nothing to add
  if (urlData['fbclid'] || urlData['gclid']) return {};
  if (!cached) return {};
  const restored: { [key: string]: string } = {};
  for (const key of CHECKOUT_RESTORABLE_KEYS) {
    if (cached[key]) restored[key] = cached[key];
  }
  // a bare timestamp with no click id to belong to describes nothing
  if (!restored['fbclid']) delete restored['fbclid_ts'];
  return restored;
}
